import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Player, ApiResponse } from '../types';
import clsx from 'clsx';

const statusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
  self_excluded: 'bg-yellow-100 text-yellow-800',
  fraud: 'bg-red-100 text-red-800',
};

export default function Players() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';

  const { data, isLoading } = useQuery({
    queryKey: ['players', { status, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('page', page.toString());

      const response = await api.get<ApiResponse<Player[]>>(`/players?${params}`);
      return response.data;
    },
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Players</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Gaming platform players referred by affiliates.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6">
        <select
          value={status}
          onChange={(e) => {
            const params = new URLSearchParams(searchParams);
            if (e.target.value) {
              params.set('status', e.target.value);
            } else {
              params.delete('status');
            }
            params.set('page', '1');
            setSearchParams(params);
          }}
          className="input max-w-xs"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="self_excluded">Self Excluded</option>
          <option value="fraud">Fraud</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">
                Player ID
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Affiliate
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Deposits
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Net Revenue
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Registered
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">
                  Loading...
                </td>
              </tr>
            ) : data?.data?.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">
                  No players found
                </td>
              </tr>
            ) : (
              data?.data?.map((player) => (
                <tr key={player.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                    <div className="font-mono text-[var(--text-primary)]">{player.externalId}</div>
                    <div className="text-[var(--text-muted)] text-xs">{player.country || 'Unknown'}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {player.affiliate?.name || '-'}
                    {player.affiliate?.referralCode && (
                      <div className="text-xs">
                        <code>{player.affiliate.referralCode}</code>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={clsx(
                        'badge',
                        statusColors[player.status as keyof typeof statusColors]
                      )}
                    >
                      {player.status}
                    </span>
                    {player.isQualified && (
                      <span className="ml-1 badge badge-success">FTD</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)]">
                    ${player.totalDeposits.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={clsx(
                        player.netRevenue >= 0 ? 'text-green-600' : 'text-red-600'
                      )}
                    >
                      ${player.netRevenue.toLocaleString()}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {new Date(player.registeredAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page - 1).toString());
                setSearchParams(params);
              }}
              disabled={page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page + 1).toString());
                setSearchParams(params);
              }}
              disabled={page >= data.pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
